import React, { useState } from 'react';
import {
  InputGroup,
  Button,
  Menu,
  MenuItem,
  Popover,
  Position,
  Tooltip,
  Icon,
} from '@blueprintjs/core';
import { FieldFilter } from '../../../../../../utils/fieldMappingSearch';
import styles from './FieldMappingsTableHeader.module.sass';

export interface FieldMappingsTableHeaderProps {
  searchQuery: string;
  fieldFilter: FieldFilter;
  onSearchChange: (query: string) => void;
  onFilterChange: (filter: FieldFilter) => void;
  showFilterTooltip?: boolean;
  onTooltipDismiss?: () => void;
  hasAutoMappedFields?: boolean;
}

/**
 * Header component for TreeFieldMappingsTable
 * Provides search and filter functionality for destination fields
 */
export const FieldMappingsTableHeader: React.FC<FieldMappingsTableHeaderProps> = ({
  searchQuery,
  fieldFilter,
  onSearchChange,
  onFilterChange,
  showFilterTooltip = false,
  onTooltipDismiss,
  hasAutoMappedFields = false,
}) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  const handlePopoverOpening = () => {
    setIsPopoverOpen(true);
  };

  const handlePopoverClosing = () => {
    setIsPopoverOpen(false);
  };

  const handleFilterChange = (filter: FieldFilter) => {
    onFilterChange(filter);
    // Dismiss tooltip when "All Fields" is selected
    if (filter === 'all' && showFilterTooltip && onTooltipDismiss) {
      onTooltipDismiss();
    }
  };

  const filterLabels: Record<FieldFilter, string> = {
    all: 'All Fields',
    required: 'Required Only',
    mapped: 'Mapped Only',
    unmapped: 'Unmapped Only',
    'auto-mapped': 'Auto-mapped Fields',
  };

  return (
    <div className={styles.searchContainer}>
      <div className={styles.searchInputWrapper}>
        <InputGroup
          leftIcon="search"
          placeholder="Filter destination fields..."
          value={searchQuery}
          onChange={(e) => onSearchChange(e.target.value)}
          rightElement={
            searchQuery ? (
              <button
                className={styles.clearButton}
                onClick={() => onSearchChange('')}
                aria-label="Clear search"
              >
                ✕
              </button>
            ) : undefined
          }
        />
      </div>
      <div className={styles.filterTooltipContainer}>
        <Tooltip
          content={
            <div className={styles.tooltipContent}>
              <span className={styles.tooltipText}>
                You can review generated fields with 'Auto-mapped fields' filter
              </span>
              <button
                className={styles.tooltipCloseButton}
                onClick={() => onTooltipDismiss && onTooltipDismiss()}
                aria-label="Close tooltip"
              >
                <Icon icon="cross" size={12} />
              </button>
            </div>
          }
          isOpen={showFilterTooltip}
          position={Position.LEFT}
          popoverClassName={styles.customDarkTooltip}
          usePortal={true}
        >
          <Popover
            position={Position.BOTTOM_RIGHT}
            onOpening={handlePopoverOpening}
            onClosing={handlePopoverClosing}
            usePortal={true}
            content={
              <Menu>
                <MenuItem
                  text="All Fields"
                  onClick={() => handleFilterChange('all')}
                  active={fieldFilter === 'all'}
                />
                <MenuItem
                  text="Required Only"
                  onClick={() => handleFilterChange('required')}
                  active={fieldFilter === 'required'}
                />
                <MenuItem
                  text="Mapped Only"
                  onClick={() => handleFilterChange('mapped')}
                  active={fieldFilter === 'mapped'}
                />
                <MenuItem
                  text="Unmapped Only"
                  onClick={() => handleFilterChange('unmapped')}
                  active={fieldFilter === 'unmapped'}
                />
                <MenuItem
                  text="Auto-mapped Fields"
                  onClick={() => handleFilterChange('auto-mapped')}
                  active={fieldFilter === 'auto-mapped'}
                  disabled={!hasAutoMappedFields}
                />
              </Menu>
            }
          >
            <Button
              icon="filter"
              rightIcon="caret-down"
              text={filterLabels[fieldFilter]}
            />
          </Popover>
        </Tooltip>
      </div>
    </div>
  );
};
